/**
 * Admin Master — Contas (Despesas / Contas a pagar do AgentUS).
 *
 * Página standalone do super_admin pra registrar despesas da empresa
 * (luz, fornecedor, marketing, infra etc.) e anexar comprovantes de
 * pagamento (PDF ou imagem). Os arquivos vivem dentro do MongoDB via
 * GridFS — resiliente a redeploys do Railway.
 *
 * Estilo: alinhado ao Admin Master existente (preto + tipografia
 * heading minimalista + linhas finas + acentos âmbar/emerald p/ estado).
 */
import { useEffect, useMemo, useState } from "react";
import {
  Plus, Receipt, MagnifyingGlass, X, FilePdf, ImageSquare,
  CheckCircle, Warning, CircleNotch, Trash, UploadSimple,
  DownloadSimple, PencilSimple, CurrencyDollar, Calendar,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const fmtBRL = (cents) =>
  (Number(cents || 0) / 100).toLocaleString("pt-BR", {
    style: "currency", currency: "BRL",
  });

const fmtDate = (iso) => {
  if (!iso) return "—";
  // iso pode ser "2026-03-15" (date) ou full ISO
  const d = iso.length === 10 ? new Date(iso + "T12:00:00") : new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const CATEGORIES = [
  { code: "infraestrutura", label: "Infraestrutura" },
  { code: "marketing",      label: "Marketing" },
  { code: "fornecedor",     label: "Fornecedor" },
  { code: "salario",        label: "Salário" },
  { code: "impostos",       label: "Impostos" },
  { code: "software",       label: "Software/SaaS" },
  { code: "outros",         label: "Outros" },
];

const STATUS_FILTERS = [
  { code: "all",     label: "Todas" },
  { code: "pending", label: "A pagar" },
  { code: "overdue", label: "Atrasadas" },
  { code: "paid",    label: "Pagas" },
];

function StatusPill({ status }) {
  const map = {
    paid:    { bg: "bg-emerald-400/10 text-emerald-400 border-emerald-400/20", label: "Paga" },
    pending: { bg: "bg-amber-300/10  text-amber-300  border-amber-300/20",  label: "A pagar" },
    overdue: { bg: "bg-rose-400/10  text-rose-400  border-rose-400/30",   label: "Atrasada" },
  };
  const s = map[status] || map.pending;
  return (
    <span className={`text-[10.5px] font-medium px-2 py-0.5 rounded border uppercase tracking-wider ${s.bg}`}>
      {s.label}
    </span>
  );
}

function ReceiptIcon({ contentType, size = 14 }) {
  if (contentType === "application/pdf") return <FilePdf size={size} weight="duotone" className="text-rose-400" />;
  if (contentType?.startsWith("image/"))  return <ImageSquare size={size} weight="duotone" className="text-sky-400" />;
  return <Receipt size={size} className="text-slate-400" />;
}

// ── Modal ────────────────────────────────────────────────────────────

function AccountModal({ account, onClose, onSaved }) {
  const isEdit = !!account?.id;
  const [form, setForm] = useState(() => ({
    title:        account?.title || "",
    description:  account?.description || "",
    vendor:       account?.vendor || "",
    category:     account?.category || "infraestrutura",
    amount_brl:   account?.amount_cents != null ? (account.amount_cents / 100).toFixed(2) : "",
    due_date:     account?.due_date || "",
    paid_date:    account?.paid_date || "",
    status:       account?.status || "pending",
    notes:        account?.notes || "",
  }));
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const valid = form.title.trim().length >= 2 && parseFloat(form.amount_brl) > 0;

  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    const payload = {
      title:        form.title.trim(),
      description:  form.description.trim() || null,
      vendor:       form.vendor.trim() || null,
      category:     form.category,
      amount_cents: Math.round(parseFloat(form.amount_brl) * 100),
      due_date:     form.due_date || null,
      paid_date:    form.paid_date || null,
      status:       form.paid_date ? "paid" : form.status,
      notes:        form.notes.trim() || null,
    };
    try {
      const { data } = isEdit
        ? await api.patch(`/admin/accounts/${account.id}`, payload)
        : await api.post(`/admin/accounts`, payload);
      toast.success(isEdit ? "Conta atualizada." : "Conta criada.");
      onSaved(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
           className="bg-slate-900 border border-white/10 rounded-lg p-6 w-full max-w-xl max-h-[92vh] overflow-y-auto"
           data-testid="account-modal">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-medium">
              {isEdit ? "Editar conta" : "Nova conta"}
            </div>
            <h2 className="font-heading text-xl font-semibold text-white mt-1">
              {isEdit ? form.title || "Conta sem título" : "Cadastrar despesa"}
            </h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="modal-close">
            <X size={18} />
          </button>
        </div>

        <div className="space-y-4">
          <Field label="Título *">
            <input
              value={form.title} onChange={(e) => set("title", e.target.value)}
              placeholder="Ex: Internet escritório - Vivo Empresas"
              data-testid="account-title"
              className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-white/30"
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Valor (R$) *">
              <input
                type="number" step="0.01" min="0"
                value={form.amount_brl} onChange={(e) => set("amount_brl", e.target.value)}
                placeholder="250,00"
                data-testid="account-amount"
                className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-white/30"
              />
            </Field>
            <Field label="Categoria">
              <select
                value={form.category} onChange={(e) => set("category", e.target.value)}
                data-testid="account-category"
                className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-white/30"
              >
                {CATEGORIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
              </select>
            </Field>
          </div>

          <Field label="Fornecedor">
            <input
              value={form.vendor} onChange={(e) => set("vendor", e.target.value)}
              placeholder="Ex: Vivo Empresas, Google Cloud, Receita Federal..."
              data-testid="account-vendor"
              className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-white/30"
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Vencimento">
              <input
                type="date" value={form.due_date} onChange={(e) => set("due_date", e.target.value)}
                data-testid="account-due-date"
                className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-white/30"
              />
            </Field>
            <Field label="Data do pagamento (preencher = marca como paga)">
              <input
                type="date" value={form.paid_date} onChange={(e) => set("paid_date", e.target.value)}
                data-testid="account-paid-date"
                className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-white/30"
              />
            </Field>
          </div>

          <Field label="Observações">
            <textarea
              rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)}
              placeholder="Notas internas (opcional)"
              data-testid="account-notes"
              className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-white/30 resize-none"
            />
          </Field>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="text-[12px] text-slate-400 hover:text-white px-3 py-1.5">
            Cancelar
          </button>
          <button
            onClick={submit} disabled={busy || !valid}
            data-testid="account-save"
            className="bg-white text-slate-900 px-4 py-1.5 rounded-md text-[12px] font-semibold hover:bg-slate-200 disabled:opacity-40"
          >
            {busy ? "Salvando…" : (isEdit ? "Salvar alterações" : "Cadastrar conta")}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-wider text-slate-400 font-medium block mb-1.5">
        {label}
      </label>
      {children}
    </div>
  );
}

// ── Detalhe lateral (drawer) ──────────────────────────────────────────

function AccountDetail({ account, onClose, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputId = `receipt-input-${account.id}`;

  const reload = async () => {
    try {
      const { data } = await api.get(`/admin/accounts/${account.id}`);
      onChanged(data);
    } catch {}
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/accounts/${account.id}/receipt`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChanged(data);
      toast.success("Comprovante anexado.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao subir arquivo.");
    } finally { setUploading(false); }
  };

  const download = () => {
    const url = `${api.defaults.baseURL}/admin/accounts/${account.id}/receipt`;
    const token = localStorage.getItem("agentos_token");
    // Faz fetch com header de auth — não dá pra usar window.open direto
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.target = "_blank";
        link.rel = "noopener";
        const fname = account.receipt?.filename || "comprovante";
        link.download = fname;
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(link.href), 5000);
      })
      .catch(() => toast.error("Erro ao baixar comprovante."));
  };

  const removeReceipt = async () => {
    if (!window.confirm("Remover o comprovante? Esta ação é irreversível.")) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/admin/accounts/${account.id}/receipt`);
      onChanged(data);
      toast.success("Comprovante removido.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro.");
    } finally { setBusy(false); }
  };

  const markPaid = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/accounts/${account.id}/pay`, {});
      onChanged(data);
      toast.success("Conta marcada como paga.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro.");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Excluir "${account.title}"? Esta ação é irreversível.`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/accounts/${account.id}`);
      onChanged(null); // sinal de deletado
      toast.success("Conta excluída.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro.");
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed top-0 right-0 bottom-0 w-full max-w-md bg-slate-900 border-l border-white/10 z-40 overflow-y-auto"
      data-testid="account-detail-drawer"
    >
      <div className="sticky top-0 bg-slate-900/95 backdrop-blur border-b border-white/5 px-5 py-3 flex justify-between items-center">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-medium">Detalhe</div>
          <div className="text-[13px] text-white font-medium truncate max-w-[280px]" title={account.title}>
            {account.title}
          </div>
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="detail-close">
          <X size={16} />
        </button>
      </div>

      <div className="p-5 space-y-5">
        {/* Resumo */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <StatusPill status={account.effective_status} />
            <div className="text-[10px] uppercase tracking-wider text-slate-500">
              {CATEGORIES.find((c) => c.code === account.category)?.label || account.category}
            </div>
          </div>
          <div className="font-heading text-3xl font-semibold text-white tabular-nums">
            {fmtBRL(account.amount_cents)}
          </div>
          {account.vendor && (
            <div className="text-[12px] text-slate-400 mt-1">{account.vendor}</div>
          )}
        </div>

        {/* Datas */}
        <div className="grid grid-cols-2 gap-3 text-[12px]">
          <Info icon={Calendar} label="Vencimento" value={fmtDate(account.due_date)} />
          <Info icon={CheckCircle} label="Pago em" value={fmtDate(account.paid_date)} />
        </div>

        {account.description && (
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Descrição</div>
            <p className="text-[13px] text-slate-300 leading-relaxed">{account.description}</p>
          </div>
        )}

        {account.notes && (
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Notas internas</div>
            <p className="text-[12.5px] text-slate-400 italic leading-relaxed">{account.notes}</p>
          </div>
        )}

        {/* Comprovante */}
        <div className="border-t border-white/5 pt-4">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Comprovante de pagamento</div>
          {account.has_receipt ? (
            <div className="bg-white/5 border border-white/10 rounded-md p-3" data-testid="receipt-panel">
              <div className="flex items-center gap-3">
                <ReceiptIcon contentType={account.receipt.content_type} size={28} />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] text-white truncate" title={account.receipt.filename}>
                    {account.receipt.filename}
                  </div>
                  <div className="text-[11px] text-slate-500">
                    {(account.receipt.size_bytes / 1024).toFixed(1)} KB · subido em{" "}
                    {fmtDate(account.receipt.uploaded_at)}
                  </div>
                </div>
              </div>
              <div className="flex gap-2 mt-3">
                <button
                  onClick={download}
                  data-testid="receipt-download"
                  className="flex-1 inline-flex items-center justify-center gap-1 bg-white/10 hover:bg-white/15 text-white text-[12px] px-3 py-1.5 rounded-md"
                >
                  <DownloadSimple size={12} /> Baixar
                </button>
                <button
                  onClick={() => document.getElementById(fileInputId).click()}
                  className="inline-flex items-center justify-center gap-1 bg-white/5 hover:bg-white/10 text-slate-300 text-[12px] px-3 py-1.5 rounded-md"
                  data-testid="receipt-replace"
                >
                  <UploadSimple size={12} /> Substituir
                </button>
                <button
                  onClick={removeReceipt} disabled={busy}
                  className="inline-flex items-center justify-center bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 text-[12px] px-3 py-1.5 rounded-md"
                  data-testid="receipt-remove"
                  title="Remover comprovante"
                >
                  <Trash size={12} />
                </button>
              </div>
            </div>
          ) : (
            <label
              htmlFor={fileInputId}
              data-testid="receipt-upload-empty"
              className="block border-2 border-dashed border-white/10 hover:border-white/30 rounded-md px-4 py-6 text-center cursor-pointer transition-colors"
            >
              {uploading ? (
                <CircleNotch size={24} className="animate-spin mx-auto text-slate-400" />
              ) : (
                <>
                  <UploadSimple size={20} className="mx-auto text-slate-400 mb-1.5" />
                  <div className="text-[12px] text-slate-300 font-medium">Anexar comprovante</div>
                  <div className="text-[10.5px] text-slate-500 mt-0.5">PDF, PNG, JPG ou WEBP — máx. 10MB</div>
                </>
              )}
            </label>
          )}
          <input
            id={fileInputId}
            type="file"
            accept="application/pdf,image/png,image/jpeg,image/webp,image/gif"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = "";  // reset pra permitir re-upload do mesmo arquivo
              if (f) upload(f);
            }}
            data-testid="receipt-input"
          />
        </div>

        {/* Ações */}
        <div className="border-t border-white/5 pt-4 flex gap-2 flex-wrap">
          {account.effective_status !== "paid" && (
            <button
              onClick={markPaid} disabled={busy}
              data-testid="account-mark-paid"
              className="inline-flex items-center gap-1.5 bg-emerald-400/10 hover:bg-emerald-400/15 text-emerald-300 border border-emerald-400/20 text-[12px] px-3 py-1.5 rounded-md"
            >
              <CheckCircle size={12} weight="duotone" /> Marcar como paga
            </button>
          )}
          <button
            onClick={remove} disabled={busy}
            data-testid="account-delete"
            className="inline-flex items-center gap-1.5 bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 border border-rose-400/20 text-[12px] px-3 py-1.5 rounded-md ml-auto"
          >
            <Trash size={12} /> Excluir
          </button>
        </div>

        <div className="text-[10px] text-slate-600 text-center pt-2">
          Criada por {account.created_by} · ID {account.id.slice(0, 8)}…
        </div>
      </div>
    </div>
  );
}

function Info({ icon: Icon, label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5 flex items-center gap-1">
        <Icon size={11} /> {label}
      </div>
      <div className="text-white">{value}</div>
    </div>
  );
}

// ── Página principal ─────────────────────────────────────────────────

export default function AdminAccounts() {
  const [accounts, setAccounts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [modal, setModal] = useState(null);     // { editing } | "new" | null
  const [selectedId, setSelectedId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: list }, { data: s }] = await Promise.all([
        api.get(`/admin/accounts`, { params: { status: filter, q: q || undefined } }),
        api.get(`/admin/accounts/summary`),
      ]);
      setAccounts(list);
      setSummary(s);
    } catch (e) {
      toast.error("Erro ao carregar contas.");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  // Search é client-side reativo a 400ms; mantém UX rápida sem hammerar a API
  useEffect(() => {
    const t = setTimeout(() => load(), 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const selected = useMemo(
    () => accounts.find((a) => a.id === selectedId) || null,
    [accounts, selectedId],
  );

  const onSaved = (acc) => {
    setModal(null);
    load();
    if (acc?.id) setSelectedId(acc.id);
  };

  const onDetailChanged = (acc) => {
    if (acc === null) {
      // deletado
      setSelectedId(null);
      load();
      return;
    }
    setAccounts((prev) => prev.map((p) => (p.id === acc.id ? acc : p)));
    load(); // refresh summary
  };

  return (
    <div className="px-4 md:px-8 py-6 md:py-7 max-w-[1400px] mx-auto" data-testid="admin-accounts-page">
      <header className="flex items-start justify-between mb-7 gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-medium">Admin Master</div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight text-white mt-1">Contas</h1>
          <p className="text-[12.5px] text-slate-400 mt-1.5 max-w-xl">
            Livro-caixa das despesas do AgentUS. Cadastre contas, anexe comprovantes (PDF/imagem)
            e mantenha o histórico financeiro auditado.
          </p>
        </div>
        <button
          onClick={() => setModal("new")}
          data-testid="account-new"
          className="inline-flex items-center gap-2 bg-white text-slate-900 px-4 py-2 rounded-md text-[13px] font-semibold hover:bg-slate-200"
        >
          <Plus size={14} weight="bold" /> Nova conta
        </button>
      </header>

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
          <SummaryCard
            label="A pagar"
            count={summary.pending.count}
            value={summary.pending.amount_cents}
            color="amber"
            icon={Warning}
            testId="summary-pending"
          />
          <SummaryCard
            label="Atrasadas"
            count={summary.overdue.count}
            value={summary.overdue.amount_cents}
            color="rose"
            icon={Warning}
            testId="summary-overdue"
          />
          <SummaryCard
            label="Pagas (mês)"
            count={summary.paid_month.count}
            value={summary.paid_month.amount_cents}
            color="emerald"
            icon={CheckCircle}
            testId="summary-paid"
          />
        </div>
      )}

      {/* Filtros */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="inline-flex items-center gap-0.5 bg-white/5 rounded-md p-0.5">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.code}
              onClick={() => setFilter(f.code)}
              data-testid={`filter-${f.code}`}
              className={`text-[12px] px-3 py-1.5 rounded transition-colors ${
                filter === f.code ? "bg-white/10 text-white" : "text-slate-400 hover:text-white"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="relative flex-1 max-w-xs">
          <MagnifyingGlass size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por título, fornecedor..."
            data-testid="account-search"
            className="w-full bg-white/5 border border-white/10 rounded-md pl-8 pr-3 py-1.5 text-[12.5px] text-white placeholder-slate-500 focus:outline-none focus:border-white/30"
          />
        </div>
      </div>

      {/* Lista */}
      {loading ? (
        <div className="text-center py-12 text-slate-500">
          <CircleNotch size={20} className="animate-spin mx-auto mb-2" /> Carregando…
        </div>
      ) : accounts.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-white/10 rounded-lg" data-testid="empty-state">
          <Receipt size={36} className="text-slate-700 mx-auto mb-3" />
          <p className="text-slate-400 text-sm">Nenhuma conta encontrada.</p>
          <button
            onClick={() => setModal("new")}
            className="mt-3 text-[12px] text-white underline underline-offset-4"
          >
            Cadastrar a primeira
          </button>
        </div>
      ) : (
        <div className="border border-white/5 rounded-lg overflow-x-auto">
        <table className="w-full min-w-[720px] text-[12.5px]" data-testid="accounts-table">
            <thead className="bg-white/[0.03] text-[10.5px] uppercase tracking-wider text-slate-400">
              <tr>
                <th className="text-left px-4 py-2.5 font-medium">Conta</th>
                <th className="text-left px-3 py-2.5 font-medium">Categoria</th>
                <th className="text-right px-3 py-2.5 font-medium">Valor</th>
                <th className="text-left px-3 py-2.5 font-medium">Vencimento</th>
                <th className="text-left px-3 py-2.5 font-medium">Status</th>
                <th className="text-center px-3 py-2.5 font-medium">Recibo</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {accounts.map((a) => (
                <tr
                  key={a.id}
                  onClick={() => setSelectedId(a.id)}
                  data-testid={`account-row-${a.id}`}
                  className={`cursor-pointer hover:bg-white/[0.03] transition-colors ${
                    selectedId === a.id ? "bg-white/[0.04]" : ""
                  }`}
                >
                  <td className="px-4 py-3">
                    <div className="text-white font-medium">{a.title}</div>
                    {a.vendor && <div className="text-[11px] text-slate-500 mt-0.5">{a.vendor}</div>}
                  </td>
                  <td className="px-3 py-3 text-slate-400 capitalize">{a.category}</td>
                  <td className="px-3 py-3 text-right text-white tabular-nums font-medium">{fmtBRL(a.amount_cents)}</td>
                  <td className="px-3 py-3 text-slate-400">{fmtDate(a.due_date)}</td>
                  <td className="px-3 py-3"><StatusPill status={a.effective_status} /></td>
                  <td className="px-3 py-3 text-center">
                    {a.has_receipt ? (
                      <ReceiptIcon contentType={a.receipt?.content_type} />
                    ) : (
                      <span className="text-slate-700">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modais */}
      {modal === "new" && (
        <AccountModal account={null} onClose={() => setModal(null)} onSaved={onSaved} />
      )}
      {modal?.editing && (
        <AccountModal account={modal.editing} onClose={() => setModal(null)} onSaved={onSaved} />
      )}
      {selected && (
        <>
          <div className="fixed inset-0 bg-black/30 z-30" onClick={() => setSelectedId(null)} />
          <AccountDetail
            account={selected}
            onClose={() => setSelectedId(null)}
            onChanged={onDetailChanged}
          />
        </>
      )}
    </div>
  );
}

function SummaryCard({ label, count, value, color, icon: Icon, testId }) {
  const cmap = {
    amber:   "border-amber-300/20  text-amber-300",
    rose:    "border-rose-400/20   text-rose-400",
    emerald: "border-emerald-400/20 text-emerald-400",
  };
  return (
    <div
      data-testid={testId}
      className={`bg-white/[0.02] border ${cmap[color]} rounded-lg p-4`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10.5px] uppercase tracking-wider text-slate-400 font-medium">{label}</div>
        <Icon size={14} className={cmap[color]} />
      </div>
      <div className="font-heading text-2xl font-semibold text-white tabular-nums">{fmtBRL(value)}</div>
      <div className="text-[11px] text-slate-500 mt-1">
        {count} {count === 1 ? "conta" : "contas"}
      </div>
    </div>
  );
}
